import React, { forwardRef, useEffect, useState } from 'react';
import { ErrorInput } from '@fx-ui/jdy-design-biz';
import { localStorageManager, getPhoneByValue } from '@libs/shared';
import classNames from 'classnames';
import AreaCode from '../area-code';
import { LOCAL_STORAGE_MIGRATE } from '../../constants';
import { getCodeByValue } from '../../utils';

interface II18nPhoneProps {
    className?: string;
    height?: string | number;
    value?: string;
    phoneDelimiter?: string;
    waterMark?: string;
    disabled?: boolean;
    phoneValid?: boolean;
    autoSelect?: boolean;
    tabIndex?: number;
    needCache?: boolean;
    onBlur?: (value: string) => void;
    onChange?: (value: string) => void;
    errMsg?: string;
    errorTrigger?: 'blur' | 'focus';
    width?: string;
    autoComplete?: boolean;
}

const I18nPhone: React.ForwardRefRenderFunction<HTMLDivElement, II18nPhoneProps> = (props, ref) => {
    const {
        className,
        height = 32,
        value = '',
        phoneDelimiter = '-',
        disabled = false,
        autoSelect = false,
        phoneValid = true,
        waterMark,
        tabIndex,
        needCache = false,
        errMsg = '',
        errorTrigger = 'focus',
        width = '100%',
        autoComplete,
        onBlur,
        onChange
    } = props;

    useEffect(() => {
        setPhoneValue(getPhoneByValue(value, phoneDelimiter));
        setAreaCode(getCodeByValue(value, needCache, phoneDelimiter));
    }, [value]);

    const [phoneValue, setPhoneValue] = useState<string>(() => {
        return getPhoneByValue(value, phoneDelimiter);
    });
    const [areaCode, setAreaCode] = useState<string>(() => {
        return getCodeByValue(value, needCache, phoneDelimiter);
    });

    const handleAreaCodeChange = (areaCodeVal: string) => {
        setAreaCode(areaCodeVal);
        needCache && localStorageManager.set(LOCAL_STORAGE_MIGRATE.SIGN_IN_PHONE_AREA, areaCodeVal);
        const value = phoneValue ? areaCodeVal + phoneDelimiter + phoneValue : '';
        // 兼容之前逻辑，onChange和onBlur同时存在时，只触发onChange
        if (onChange) {
            onChange(value);
        } else if (onBlur) {
            // 当只传入onBlur事件时触发onBlur事件
            onBlur(value);
        }
    };

    const getBeforeAddon = () => {
        return (
            <AreaCode
                areaCode={areaCode}
                disabled={disabled}
                height={height}
                onAreaCodeChange={handleAreaCodeChange}
            />
        );
    };

    return (
        <div className={classNames('fx-i18n-phone', className)}>
            <ErrorInput
                ref={ref}
                autoComplete={autoComplete}
                errMsg={errMsg}
                errorTrigger={errorTrigger}
                className="phone-input"
                placeholder={waterMark}
                inputState={phoneValid ? 'normal' : 'error'}
                value={phoneValue}
                disabled={disabled}
                beforeAddon={getBeforeAddon()}
                autoSelect={autoSelect}
                tabIndex={tabIndex}
                style={{
                    height,
                    width
                }}
                onChange={(val) => {
                    const inputValue = val.replace(/\D/g, '');
                    setPhoneValue(inputValue);
                    if (onChange) {
                        const value = inputValue ? areaCode + phoneDelimiter + inputValue : '';
                        onChange(value);
                    }
                }}
                onBlur={(val) => {
                    if (disabled) {
                        return;
                    }
                    if (onBlur) {
                        const phoneStr = val ? areaCode + phoneDelimiter + val : '';
                        onBlur(phoneStr);
                    }
                }}
            />
        </div>
    );
};

export default forwardRef(I18nPhone);
